import axios from 'axios'
import { getCookie, setCookie, eraseCookie } from '../api/helpers'

// Track last activity time globally
let lastActivityTime = Date.now();
let inactivityLogoutCallback = null;

export const updateLastActivity = () => {
  lastActivityTime = Date.now();
};

export const setInactivityLogoutCallback = (callback) => {
  inactivityLogoutCallback = callback;
};

let client = ''
const pathname = window.location.pathname.split('/')
if (pathname.length > 1) {  client = pathname[1]}

let api_url = process.env.REACT_APP_API_URL

let baseURL
if (api_url) {
  baseURL = `${api_url}/${client}/api`
} else {
  baseURL = `${window.location.origin}/backend/${client}/api`
}

const axiosApi = axios.create({
  baseURL: baseURL
})

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

axiosApi.interceptors.request.use(
  config => {
    config.headers['X-API-ENDPOINT'] = 1;
    const token = getCookie('access');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  }
);

axiosApi.interceptors.response.use(
  (response) => response, 
  async (error) => {
    const originalRequest = error.config;
    const pathname = window.location.pathname.split('/');
    let client = '';
    if (pathname.length > 1) { client = pathname[1]; }

    if (error?.response?.status === 401 && !originalRequest._retry) {
      // Check if user has been inactive for more than 1 minute
      const now = Date.now();
      const inactivityTime = now - lastActivityTime;
      const maxInactivityTime = 15 * 60 * 1000; // 15 minutes
      
      if (inactivityTime > maxInactivityTime) {
        eraseCookie("access");
        eraseCookie("refresh");
        
        if (inactivityLogoutCallback) {
          inactivityLogoutCallback();
        } else {
          // Fallback to redirect if no callback set
          if (window.location.pathname !== `/${client}` && !window.location.pathname.startsWith(`/${client}/forgot-password`) && !window.location.pathname.startsWith(`/${client}/password-reset`)) {
            window.location.replace(`/${client}`);
          }
        }
        return Promise.reject(new Error('Session expired due to inactivity'));
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axiosApi(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = getCookie('refresh');
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        const response = await axios.post(`${baseURL}/accounts/token/refresh/`, {
          refresh: refreshToken
        }, {
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": getCookie('csrftoken'),
          },
        });

        const { access, refresh } = response.data;
        setCookie("access", access);
        setCookie("refresh", refresh);
        
        processQueue(null, access);
        originalRequest.headers.Authorization = `Bearer ${access}`;
        
        return axiosApi(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        eraseCookie("access");
        eraseCookie("refresh");
        
        if (window.location.pathname !== `/${client}` && !window.location.pathname.startsWith(`/${client}/forgot-password`) && !window.location.pathname.startsWith(`/${client}/password-reset`)) {
          window.location.replace(`/${client}`);
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    if (error?.response?.status === 401) {
      if (window.location.pathname !== `/${client}` && !window.location.pathname.startsWith(`/${client}/forgot-password`) && !window.location.pathname.startsWith(`/${client}/password-reset`)) {
        window.location.replace(`/${client}`);
      }
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
)

export { axiosApi }
